import { useRef } from "react";
import {Chart as ChartJS,CategoryScale,LinearScale,BarElement,Title,Tooltip,Legend,ChartOptions,ChartData,} from "chart.js";
import { Bar } from "react-chartjs-2";
import EqualizerOutlinedIcon from "@mui/icons-material/EqualizerOutlined";
import StackKit from "@/shared/components/kit/stack";
import { Box } from "@mui/material";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { ListMerchantData } from "@/types/api/merchant/merchant.type";
import BoxKit from "@/shared/components/kit/box";
import { numberSeparator } from "@/hooks/useThousandsSeparator";

ChartJS.register(CategoryScale,LinearScale,BarElement,Title,Tooltip,Legend);

type ChartDataItem = {
  date: string;
  cache: string | number;
  credit: number;
};

type ColumnChartsProps = {
  categories?: string[];
  series?: any;
  barWidth?: string;
  colors?: string[];
  dataAssets?: ListMerchantData | undefined;
  chartData?: ChartDataItem[];
  colorSideBar?: string;
  isSidebar?: boolean;
  setActiveTab?: any;
  activeTab?: string;
  setSelected?: any;
  setSelectedTypeChart?: any;
};
/**
 * ColumnCharts
 *
 * کامپوننت نمایش نمودارهای ستونی با استفاده از Chart.js و react-chartjs-2.
 * این کامپوننت داده‌های فروش نقدی و اعتباری را به صورت داینامیک پردازش کرده
 * و با دو گرادیان مجزا در یک نمودار میله‌ای ارائه می‌دهد.
 *
 * ویژگی‌ها:
 * - پشتیبانی از دو حالت هفتگی و ماهانه با سوییچ تب.
 * - تولید گرادیان داینامیک متناسب با اندازه نمودار.
 * - پردازش داده‌های ورودی شامل تاریخ، مبلغ نقدی و اعتباری.
 * - ریسپانسیو در موبایل و دسکتاپ.
 * - سفارشی‌سازی رنگ‌ها، عرض میله‌ها، و رفتار تولتیپ و محور‌ها.
 * - قابلیت نمایش سایدبار رنگی برای نمایش انواع اعتبارات و فروش‌ها.
 *
 * @param categories برچسب‌های افقی در صورت نبود داده API
 * @param series سری داده‌ها در حالت ورودی مستقیم بدون پردازش API
 * @param barWidth درصد پهنای میله‌های نمودار
 * @param colors رنگ‌های سفارشی برای هر سری داده
 * @param dataAssets داده‌های دریافت شده از API
 * @param chartData داده‌های خام شامل تاریخ، مبلغ نقدی و اعتباری
 * @param colorSideBar رنگ فونت محور y در حالت نمایش سایدبار
 * @param isSidebar فعال‌سازی حالت نمایش نوار راهنما در سمت چپ
 * @param activeTab تب فعال فعلی (هفتگی | ماهانه)
 * @param setActiveTab تابع تغییر تب فعال
 * @param setSelected مقدار انتخابی برای فیلتر داده‌ها
 * @param setSelectedTypeChart مقدار انتخابی برای نوع نمودار
 *
 * @returns JSX.Element کامپوننت نمودار ستونی با قابلیت تعامل و سفارشی‌سازی
 */

const ColumnCharts = (props: ColumnChartsProps) => {
  const { categories, series, barWidth = "55%", colors, dataAssets, chartData, colorSideBar, isSidebar, activeTab, setActiveTab, setSelected, setSelectedTypeChart, } = props;
  const chartRef = useRef<ChartJS<"bar", number[], string>>(null);

  const tabs = [
    { id: 4, label: "هفتگی", value: "weekly" },
    { id: 3, label: "ماهانه", value: "monthly" },
  ];
  const createCacheGradient = (ctx: CanvasRenderingContext2D,chartArea: any) => {
    const gradient = ctx.createLinearGradient(0,chartArea.top,0,chartArea.bottom);
    gradient.addColorStop(0, "#EB6440");
    gradient.addColorStop(1, "#853924");
    return gradient;
  };

  const createCreditGradient = (ctx: CanvasRenderingContext2D,chartArea: any) => {
    const gradient = ctx.createLinearGradient(0,chartArea.top,0,chartArea.bottom);
    gradient.addColorStop(0, "#A1F5FF");
    gradient.addColorStop(1, "#619399");
    return gradient;
  };

  const processChartData = (data: ChartDataItem[]): ChartData<"bar", number[], string> => {
    if (!data || data.length === 0) {
      if (series && categories) {
        return {
          labels: categories,
          datasets: series.map((item: any, index: number) => ({
            label: item.name || `داده ${index + 1}`,
            data: item.data || [],
            backgroundColor:colors?.[index] || (index === 0 ? "#EB6440" : "#A1F5FF"),
            borderColor:colors?.[index] || (index === 0 ? "#EB6440" : "#A1F5FF"),
            borderWidth: 1,
            borderRadius: 5,
            borderSkipped: false as const,
          })),
        };
      }

      return {
        labels: [],
        datasets: [],
      };
    }

    const processedLabels = data.map((item) => {
      const date = new Date(item.date);
      return date.toLocaleDateString("fa-IR", {
        month: "long",
        day: "numeric",
      });
    });

    const cacheData = data.map((item) => {
      const cacheValue =
        typeof item.cache === "string" ? parseInt(item.cache) : item.cache;
      return cacheValue || 0;
    });

    const creditData = data.map((item) => item.credit || 0);

    return {
      labels: processedLabels,
      datasets: [
        {
          label: "نقدی",
          data: cacheData,
          backgroundColor: function (context: any) {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return "#EB6440";
            return createCacheGradient(ctx, chartArea);
          } as any,
          borderColor: "#EB6440",
          borderWidth: 1,
          borderRadius: 5,
          borderSkipped: false as const,
        },
        {
          label: "اعتباری",
          data: creditData,
          backgroundColor: function (context: any) {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return "#A1F5FF";
            return createCreditGradient(ctx, chartArea);
          } as any,
          borderColor: "#A1F5FF",
          borderWidth: 1,
          borderRadius: 5,
          borderSkipped: false as const,
        },
      ],
    };
  };

  const chartData_processed = processChartData(chartData || []);

  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          usePointStyle: true,
          font: {
            family: 'vaziriFont, "B Yekan", Tahoma, Arial, sans-serif',
            size: 14,
          },
          color: "#757575",
        },
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#fff",
        bodyColor: "#fff",
        titleFont: {
          family: 'vaziriFont, "B Yekan", Tahoma, Arial, sans-serif',
          size: 14,
        },
        bodyFont: {
          family: 'vaziriFont, "B Yekan", Tahoma, Arial, sans-serif',
          size: 12,
        },
        callbacks: {
          label: function (context) {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${value.toLocaleString(
              "fa-IR"
            )} ریال`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            family: 'vaziriFont, "B Yekan", Tahoma, Arial, sans-serif',
            size: 14,
          },
          color: "#757575",
        },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "",
          font: {
            family: "Arial, sans-serif",
            size: 14,
            weight: "bold",
          },
          color: colorSideBar ? colorSideBar : "#757575",
        },
        ticks: {
          font: {
            family: "Arial, sans-serif",
            size: 12,
          },
          precision: 0,
          color: colorSideBar ? colorSideBar : "#757575",
          callback: function (value) {
            return numberSeparator(value);
          },
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
    },
    datasets: {
      bar: {
        barPercentage: 0.3,
        categoryPercentage: 0.4,
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
    animation: {
      duration: 1000,
      easing: "easeInOutQuart",
    },
  };

  return (
    <Box sx={{ direction: "rtl", width: "100%" }}>
      {/* Header / Tabs */}
      <Box sx={{ width: "100%", mb: 2 }}>
        <StackKit direction={{ xs: "column", sm: "row" }} justifyContent={{ xs: "flex-start", sm: "start" }} gap={2} flexWrap="wrap">
          <StackKit direction="row" justifyContent="start" gap={2}>
            <EqualizerOutlinedIcon />
            <TypoGraphyKit variant="body1">گزارش ها تراکنش دستگاه</TypoGraphyKit>
          </StackKit>
          <StackKit direction="row" justifyContent="start" sx={{ bgcolor: "#5A5A5A", padding: "10px", borderRadius: "8px", flexWrap: "wrap", overflowX: { xs: "auto", sm: "visible" }, }} alignItems="center">
            {tabs.map((tab) => (
              <StackKit key={tab.id} onClick={() => { setActiveTab(tab.label); setSelectedTypeChart(tab.value);}}
                sx={{
                  padding: "6px 16px",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  borderRadius: "8px",
                  bgcolor: activeTab === tab.label ? "#434343" : "transparent",
                  color: activeTab === tab.label ? "#fff" : "#B3B3B3",
                  border: activeTab === tab.label ? "1px solid #434343" : "1px solid transparent",
                  transition: "all 0.2s ease",
                  "&:hover": { bgcolor: activeTab === tab.label ? "#434343" : "#666666", },
                  flexShrink: 0,
                }}
              >
                <TypoGraphyKit variant="body2" sx={{ color: "inherit" }}>
                  {tab.label}
                </TypoGraphyKit>
              </StackKit>
            ))}
          </StackKit>
        </StackKit>
      </Box>

      {/* Chart Area */}
      <StackKit direction={{ xs: "column", md: "row" }} sx={{ width: "100%", overflowX: "auto", gap: { xs: 2, md: 0 } }}>
        {isSidebar && (
          <StackKit direction="column" sx={{width: { xs: "100%", md: "200px" },minWidth: { xs: "100%", md: "200px" },borderRadius: "12px",justifyContent: "space-between",gap: 1,}}>
            {[
              { color: "linear-gradient(180deg, #AF0000 0%, #490000 100%)", label: "اعتبار هدیه" },
              { color: "linear-gradient(180deg, #7B9D95 0%, #2B3734 100%)", label: "اعتبار داخلی" },
              { color: "linear-gradient(180deg, #7BC8B5 0%, #3C6259 100%)", label: "اعتبار خارجی" },
              { color: "linear-gradient(180deg, #EB6440 0%, #853924 100%)", label: "فروش نقدی خارجی" },
              { color: "linear-gradient(180deg, #F9C4B5 0%, #3E1105 100%)", label: "فروش نقدی داخلی" },
              { color: "linear-gradient(180deg, #A2F5FF 0%, #619399 100%)", label: "فروش IPG ( انلاین)" },
            ].map((item, idx) => (
              <StackKit key={idx} direction="row" alignItems="center" justifyContent="start" gap={1}>
                <BoxKit sx={{ width: "20px", height: "36px", borderRadius: "50px", background: item.color, }} />
                <TypoGraphyKit variant="body1">{item.label}</TypoGraphyKit>
              </StackKit>
            ))}
          </StackKit>
        )}

        {/* Chart Box */}
        <Box sx={{ flex: 1, mt: { xs: 2, md: 0 }, height: { xs: "300px", md: "350px" }, border: "none", boxShadow: "none", backgroundColor: "transparent", minWidth: 0, }}>
          <Bar ref={chartRef} data={chartData_processed} options={options} />
        </Box>
      </StackKit>
    </Box>
  );
};

export default ColumnCharts;
